/**
 * Semantic matching for "About this page" search.
 *
 * Task chunks are embedded at build time (scripts/build-page-info-embeddings.ts)
 * into `index/<lang>.json`, loaded on the first search. Only the reader's query
 * is embedded at search time, by the `page-info-embed` Edge Function
 * (franklin-data-pipeline), which holds the OpenAI key.
 *
 * Anything that goes wrong here — function not deployed yet, no key, offline,
 * a model mismatch — degrades to keyword-only search; it never breaks the dialog.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Language } from "@/lib/i18n/types";
import type { PageInfoChunk } from "./pageInfoChunks";
import {
  PAGE_INFO_EMBED_DIMENSIONS,
  PAGE_INFO_EMBED_FUNCTION,
  PAGE_INFO_EMBED_MODEL,
  type PageInfoIndexFile,
} from "./embedConfig";

/**
 * Which semantic hits count as related. A hit must clear the index's noise
 * floor (what unrelated queries reach), stand out from this query's own score
 * spread (z ≥ SEMANTIC_MIN_Z), and sit near the best hit. Calibrated with
 * text-embedding-3-large at 1024 dimensions: on a hand-written set of
 * paraphrased tasks and off-topic queries per language, this finds 8/9
 * English and 6/6 Estonian paraphrases while 1 of 10 off-topic queries per
 * language returns anything. Recalibrate if the model or the copy changes a lot.
 */
export const SEMANTIC_MIN_Z = 2.5;
export const SEMANTIC_BEST_WINDOW = 0.08;
export const SEMANTIC_LIMIT = 5;

export type SemanticIndex = Map<string, Float32Array>;

const indexLoaders: Record<Language, () => Promise<{ default: PageInfoIndexFile }>> = {
  en: () => import("./index/en.json"),
  et: () => import("./index/et.json"),
};

function normalise(vector: Float32Array): Float32Array {
  let norm = 0;
  for (const value of vector) norm += value * value;
  norm = Math.sqrt(norm);
  if (norm === 0) return vector;
  for (let i = 0; i < vector.length; i++) vector[i] /= norm;
  return vector;
}

export function decodeVector(base64: string): Float32Array {
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const signed = new Int8Array(bytes.buffer);
  return normalise(Float32Array.from(signed));
}

/**
 * Vectors for the chunks whose text still matches what was embedded. A chunk
 * edited since the last rebuild has no vector, so it is found by keyword only
 * rather than ranked by a vector for words it no longer says.
 */
export function buildSemanticIndex(
  file: PageInfoIndexFile,
  chunks: readonly PageInfoChunk[]
): SemanticIndex | null {
  if (file.model !== PAGE_INFO_EMBED_MODEL || file.dimensions !== PAGE_INFO_EMBED_DIMENSIONS) {
    return null;
  }
  const index: SemanticIndex = new Map();
  for (const chunk of chunks) {
    const entry = file.chunks[chunk.key];
    if (entry && entry.hash === chunk.hash) index.set(chunk.key, decodeVector(entry.v));
  }
  return index;
}

const loadedFiles = new Map<Language, Promise<PageInfoIndexFile>>();

export function loadIndexFile(language: Language): Promise<PageInfoIndexFile> {
  let pending = loadedFiles.get(language);
  if (!pending) {
    pending = indexLoaders[language]().then((module) => module.default);
    // A failed chunk load must not stick: the next search retries it.
    pending.catch(() => loadedFiles.delete(language));
    loadedFiles.set(language, pending);
  }
  return pending;
}

const queryCache = new Map<string, Promise<Float32Array>>();

/** The query's embedding, normalised. Repeated queries cost one call. */
export function embedQuery(query: string): Promise<Float32Array> {
  const key = query.trim().toLowerCase();
  let pending = queryCache.get(key);
  if (!pending) {
    pending = supabase.functions
      .invoke(PAGE_INFO_EMBED_FUNCTION, { body: { text: query.trim() } })
      .then(({ data, error }) => {
        if (error) throw error;
        const payload = data as { model?: string; dimensions?: number; embedding?: unknown };
        if (
          payload?.model !== PAGE_INFO_EMBED_MODEL ||
          payload.dimensions !== PAGE_INFO_EMBED_DIMENSIONS ||
          !Array.isArray(payload.embedding)
        ) {
          throw new Error("page-info-embed answered with an unexpected model or shape");
        }
        return normalise(Float32Array.from(payload.embedding as number[]));
      });
    pending.catch(() => queryCache.delete(key));
    queryCache.set(key, pending);
  }
  return pending;
}

export interface SemanticHit {
  chunk: PageInfoChunk;
  score: number;
}

export function semanticSearch(
  index: SemanticIndex,
  query: Float32Array,
  chunks: readonly PageInfoChunk[],
  noiseFloor: number
): SemanticHit[] {
  const scored: SemanticHit[] = [];
  for (const chunk of chunks) {
    const vector = index.get(chunk.key);
    if (!vector) continue;
    let score = 0;
    for (let i = 0; i < vector.length; i++) score += vector[i] * query[i];
    scored.push({ chunk, score });
  }
  if (scored.length === 0) return [];

  const mean = scored.reduce((sum, hit) => sum + hit.score, 0) / scored.length;
  const spread = Math.sqrt(
    scored.reduce((sum, hit) => sum + (hit.score - mean) ** 2, 0) / scored.length
  );
  scored.sort((a, b) => b.score - a.score);
  const best = scored[0].score;
  return scored
    .filter(
      (hit) =>
        hit.score >= noiseFloor &&
        spread > 0 &&
        (hit.score - mean) / spread >= SEMANTIC_MIN_Z &&
        hit.score >= best - SEMANTIC_BEST_WINDOW
    )
    .slice(0, SEMANTIC_LIMIT);
}
